import { combine, createEffect, createEvent, createStore, sample, scopeBind } from "effector";
import Cookies from "js-cookie";
import { ModelContract, ModuleDescription } from "@features/shared/module-description";
import { appMounted, routePathNameChanged } from "@features/app/model";
import { AnalyticsService } from "@features/shared/analytics";
import { themeChanged, themeSwitcherPresented as trackThemeSwitcherPresented } from "@features/shared/events";
import { getCookieDomain } from "@utils/getCookieDomain";
import type { IUserAuthContract } from "@features/student/auth/model";
import type { IMetadataContract } from "@features/student/metadata/model";
import type { IAmplitudeFlagsContract } from "@features/amplitude-flags/model";
import { isPinnedLightPath } from "../pinned-routes";
import {
    DARK_CLASS,
    THEME_COLOR_DARK,
    THEME_COLOR_LIGHT,
    THEME_COOKIE,
    THEME_ENABLED_COOKIE,
    THEME_METADATA_KEY,
    isThemePreference,
    type ResolvedTheme,
    type ThemePreference
} from "../init-script";

export type { ResolvedTheme, ThemePreference };
export type ThemePlace = "profile" | "landing_header" | "landing_menu";

type ResolveInput = { preference: ThemePreference; systemDark: boolean; enabled: boolean; locked: boolean };

/** The one rule: flag off or a light-pinned surface wins, then the preference, with "auto" following the OS. */
export const resolveTheme = ({ preference, systemDark, enabled, locked }: ResolveInput): ResolvedTheme => {
    if (!enabled || locked) return "light";
    if (preference === "auto") return systemDark ? "dark" : "light";
    return preference;
};

type PreferenceChange = {
    from: ThemePreference;
    to: ThemePreference;
    place: ThemePlace;
    resolved: ResolvedTheme;
    systemDark: boolean;
};

// Events
const themePreferenceChanged = createEvent<{ preference: ThemePreference; place: ThemePlace }>();
const themeSwitcherPresented = createEvent<ThemePlace>();
const themeLockAcquired = createEvent<string>();
const themeLockReleased = createEvent<string>();
const preferenceApplied = createEvent<PreferenceChange>();
const preferenceRestored = createEvent<ThemePreference>();
const flagResolved = createEvent<boolean>();
const systemPrefersDarkChanged = createEvent<boolean>();

// Stores
const $themePreference = createStore<ThemePreference>("light")
    .on(preferenceApplied, (_, change) => change.to)
    .on(preferenceRestored, (_, preference) => preference);
/** True once a preference is known for this device (cookie or an explicit choice); metadata only fills the gap. */
const $hasStoredPreference = createStore(false)
    .on(preferenceApplied, () => true)
    .on(preferenceRestored, () => true);
const $systemPrefersDark = createStore(false).on(systemPrefersDarkChanged, (_, matches) => matches);
/** Seeded from the guard cookie (what the pre-paint script believed), then overwritten by the flag. */
const $darkModeEnabled = createStore(false).on(flagResolved, (_, enabled) => enabled);
const $pathname = createStore<string | null>(null).on(routePathNameChanged, (_, pathname) => pathname);
const $componentLocks = createStore<string[]>([])
    .on(themeLockAcquired, (locks, id) => (locks.includes(id) ? locks : [...locks, id]))
    .on(themeLockReleased, (locks, id) => locks.filter((lock) => lock !== id));
const $themeLocked = combine(
    $pathname,
    $componentLocks,
    (pathname, locks) => (pathname !== null && isPinnedLightPath(pathname)) || locks.length > 0
);
/** Exported for code outside the contract pattern (e.g. lesson image surfaces); UI should prefer the contract. */
export const $resolvedTheme = combine(
    { preference: $themePreference, systemDark: $systemPrefersDark, enabled: $darkModeEnabled, locked: $themeLocked },
    resolveTheme
);

// Effects
const hydrateThemeFx = createEffect(() => {
    const cookie = Cookies.get(THEME_COOKIE);
    return {
        preference: isThemePreference(cookie) ? cookie : null,
        guardOn: Cookies.get(THEME_ENABLED_COOKIE) === "1",
        pathname: typeof location !== "undefined" ? location.pathname : "/"
    };
});

const cookieOptions = () => {
    const domain = getCookieDomain();
    return { path: "/", ...(domain ? { domain } : {}) };
};

// Same shape as persistLanguage (i18n/index.ts): drop a host-only cookie, then write on the root domain.
const persistThemeCookieFx = createEffect((preference: ThemePreference) => {
    Cookies.remove(THEME_COOKIE, { path: "/" });
    Cookies.set(THEME_COOKIE, preference, { ...cookieOptions(), expires: 365 });
});

const persistGuardCookieFx = createEffect((enabled: boolean) => {
    const options = cookieOptions();
    if (enabled) {
        Cookies.set(THEME_ENABLED_COOKIE, "1", { ...options, expires: 365 });
    } else {
        Cookies.remove(THEME_ENABLED_COOKIE, { path: "/" });
        Cookies.remove(THEME_ENABLED_COOKIE, options);
    }
});

const applyThemeToDomFx = createEffect((theme: ResolvedTheme) => {
    if (typeof document === "undefined") return;
    const dark = theme === "dark";
    const html = document.documentElement;
    html.classList.toggle(DARK_CLASS, dark);
    html.style.colorScheme = dark ? "dark" : "light";
    document
        .querySelector('meta[name="theme-color"]')
        ?.setAttribute("content", dark ? THEME_COLOR_DARK : THEME_COLOR_LIGHT);
    document.querySelector('meta[name="color-scheme"]')?.setAttribute("content", dark ? "dark" : "light");
});

const watchSystemThemeFx = createEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return false;
    const media = window.matchMedia("(prefers-color-scheme: dark)");
    // The listener fires outside any effector call chain: bind it to the scope that started the watch.
    const emit = scopeBind(systemPrefersDarkChanged, { safe: true });
    media.addEventListener("change", (event) => emit(event.matches));
    return media.matches;
});

const trackThemeChangedFx = createEffect((change: PreferenceChange) => {
    themeChanged({
        from: change.from,
        to: change.to,
        place: change.place,
        resolved_theme: change.resolved,
        system_prefers_dark: change.systemDark
    });
    AnalyticsService.setUserProps({ theme_preference: change.to, resolved_theme: change.resolved });
});

const trackSwitcherPresentedFx = createEffect((place: ThemePlace) => {
    trackThemeSwitcherPresented(place);
});

const setThemeUserPropsFx = createEffect(({ preference, resolved }: { preference: ThemePreference; resolved: ResolvedTheme }) => {
    AnalyticsService.setUserProps({ theme_preference: preference, resolved_theme: resolved });
});

// Wiring
sample({ clock: appMounted, target: [hydrateThemeFx, watchSystemThemeFx] });

$systemPrefersDark.on(watchSystemThemeFx.doneData, (_, matches) => matches);
$themePreference.on(hydrateThemeFx.doneData, (current, { preference }) => preference ?? current);
$hasStoredPreference.on(hydrateThemeFx.doneData, (current, { preference }) => current || preference !== null);
$darkModeEnabled.on(hydrateThemeFx.doneData, (_, { guardOn }) => guardOn);
// $activeRoute is still "/" when appMounted fires (Providers runs the mount effect first), so read the URL.
$pathname.on(hydrateThemeFx.doneData, (current, { pathname }) => current ?? pathname);

// One sample reads the previous preference before the store moves, so analytics gets a true "from".
sample({
    clock: themePreferenceChanged,
    source: { prev: $themePreference, systemDark: $systemPrefersDark, enabled: $darkModeEnabled, locked: $themeLocked },
    fn: ({ prev, systemDark, enabled, locked }, { preference, place }): PreferenceChange => ({
        from: prev,
        to: preference,
        place,
        systemDark,
        resolved: resolveTheme({ preference, systemDark, enabled, locked })
    }),
    target: preferenceApplied
});
sample({ clock: preferenceApplied, fn: (change) => change.to, target: persistThemeCookieFx });
sample({ clock: preferenceApplied, target: trackThemeChangedFx });
sample({ clock: preferenceRestored, target: persistThemeCookieFx });
sample({ clock: themeSwitcherPresented, target: trackSwitcherPresentedFx });

sample({ clock: $resolvedTheme.updates, target: applyThemeToDomFx });
// Re-sync once hydration settles even when nothing changed, so the DOM agrees with the pre-paint script.
sample({ clock: hydrateThemeFx.done, source: $resolvedTheme, target: applyThemeToDomFx });

const contract = new ModelContract(
    { $themePreference, $resolvedTheme, $systemPrefersDark, $darkModeEnabled, $themeLocked, $componentLocks },
    { themePreferenceChanged, themeSwitcherPresented, themeLockAcquired, themeLockReleased },
    { hydrateThemeFx, applyThemeToDomFx, persistThemeCookieFx, persistGuardCookieFx, watchSystemThemeFx },
    {}
);

export type IThemeContract = typeof contract;

export class ThemeModule extends ModuleDescription<IThemeContract> {
    constructor(
        public userAuthModule: IUserAuthContract,
        public metadataModule: IMetadataContract,
        public amplitudeFlagsModule: IAmplitudeFlagsContract
    ) {
        super();
    }

    getDefaultContract() {
        return contract;
    }

    run() {
        const $flag = this.amplitudeFlagsModule.stores.$darkModeEnabled;

        // The flag mirrors into the model and the guard cookie once PostHog has answered (null = not yet).
        sample({
            clock: [$flag.updates, appMounted],
            source: $flag,
            filter: (enabled): enabled is boolean => enabled !== null,
            target: flagResolved
        });
        sample({ clock: flagResolved, target: persistGuardCookieFx });
        sample({
            clock: flagResolved,
            source: { preference: $themePreference, resolved: $resolvedTheme },
            filter: (_, enabled) => enabled,
            target: setThemeUserPropsFx
        });

        // Signed-in users keep their choice on the account too, so a new device can pick it up.
        sample({
            clock: preferenceApplied,
            source: this.userAuthModule.stores.$student,
            filter: (student) => !!student,
            fn: (_, change) => ({ key: THEME_METADATA_KEY, value: change.to }),
            target: this.metadataModule.events.setMetadataRequested
        });

        // No cookie on this device yet: adopt the account's choice and persist it for the next pre-paint.
        sample({
            clock: this.metadataModule.events.metadataLoaded,
            source: $hasStoredPreference,
            filter: (hasStored, response) => !hasStored && isThemePreference(response.metadata?.[THEME_METADATA_KEY]),
            fn: (_, response) => response.metadata[THEME_METADATA_KEY] as ThemePreference,
            target: preferenceRestored
        });
    }
}
